// AGC: VoiceContext - Global voice state management
// AGC: Manages voice configuration, queue state, and auto-sync on reconnect

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useSubscription } from './SubscriptionContext'
import * as voiceQueue from '../services/voiceQueue'
import type { VoiceConfig, VoiceTranscriptionMode, VoiceResult, QueuedVoiceCommand } from '../types/admin'
import type { SubscriptionTier } from '../types/subscription'
import { getBackendUrl } from '../utils/backendUrl'

/**
 * AGC: Determine effective voice mode based on subscription tier and quota
 * AGC: FREE users use Whisper until monthly quota exhausted, then native fallback
 * AGC: PRO/PREMIUM users always get Mode A when online
 */
// AGC: configuredMode kept for future PRO user mode preference support
function getEffectiveVoiceMode(
  tier: SubscriptionTier,
  configuredMode: VoiceTranscriptionMode,
  isOnline: boolean,
  whisperExhausted: boolean
): VoiceTranscriptionMode {
  // AGC: Offline always falls back to native
  if (!isOnline) return 'B'
  // AGC: FREE tier — use Whisper until quota exhausted, then native
  if (tier === 'free') {
    return whisperExhausted ? 'B' : 'A'
  }
  // AGC: PRO/PREMIUM — always Whisper when online
  return 'A'
}

/**
 * AGC: Default voice configuration (Mode B = Native/Free)
 */
const DEFAULT_VOICE_CONFIG: VoiceConfig = {
  transcriptionMode: 'B',
  enableGPTFallback: true,
  enableOfflineQueue: true,
  maxQueueSize: 50,
  autoProcessQueue: true,
  defaultLanguage: 'pt-BR',
}

/**
 * AGC: Voice context type
 */
interface VoiceContextType {
  // AGC: Configuration
  config: VoiceConfig
  mode: VoiceTranscriptionMode
  setMode: (mode: VoiceTranscriptionMode) => Promise<void>
  updateConfig: (updates: Partial<VoiceConfig>) => Promise<void>
  reloadConfig: () => Promise<void>

  // AGC: Online/offline state
  isOnline: boolean

  // AGC: Whisper quota state
  whisperExhausted: boolean
  markWhisperExhausted: () => void

  // AGC: Queue management
  queueCount: number
  queuedCommands: QueuedVoiceCommand[]
  processQueue: () => Promise<{ processed: number; failed: number }>
  clearQueue: () => void

  // AGC: Last result (for UI feedback)
  lastResult: VoiceResult | null
  setLastResult: (result: VoiceResult | null) => void

  // AGC: Loading/error states
  isLoading: boolean
  isProcessingQueue: boolean
  error: string | null
  clearError: () => void
}

/**
 * AGC: Create context
 */
const VoiceContext = createContext<VoiceContextType | undefined>(undefined)

/**
 * AGC: Provider props
 */
interface VoiceProviderProps {
  children: ReactNode
  backendUrl?: string
}

/**
 * AGC: VoiceProvider - Wraps app with voice state management
 */
export const VoiceProvider: React.FC<VoiceProviderProps> = ({ 
  children,
  backendUrl = getBackendUrl(),
}) => {
  // AGC: Auth context
  const { user, getAuthToken } = useAuth()
  const userId = user?.uid || ''
  
  // AGC: Get subscription tier for voice mode enforcement
  const { tier: subscriptionTier } = useSubscription()

  // AGC: State
  const [config, setConfig] = useState<VoiceConfig>(DEFAULT_VOICE_CONFIG)
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  )
  const [whisperExhausted, setWhisperExhausted] = useState<boolean>(() => {
    // AGC: Check localStorage for month-scoped exhaustion flag
    const monthKey = new Date().toISOString().slice(0, 7)
    return localStorage.getItem(`droidfarm_whisperExhausted_${monthKey}`) === 'true'
  })
  const [queueCount, setQueueCount] = useState<number>(0)
  const [queuedCommands, setQueuedCommands] = useState<QueuedVoiceCommand[]>([])
  const [lastResult, setLastResult] = useState<VoiceResult | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isProcessingQueue, setIsProcessingQueue] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // AGC: Load config from backend
  const reloadConfig = useCallback(async () => {
    setIsLoading(true)
    try {
      const token = await getAuthToken()
      if (!token) {
        console.warn('⚠️ AGC: No auth token for voice config')
        setIsLoading(false)
        return
      }

      const response = await fetch(`${backendUrl}/api/v1/voice/config`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })

      if (response.ok) {
        const data = await response.json()
        setConfig({ ...DEFAULT_VOICE_CONFIG, ...data })
        console.log('✅ AGC: Voice config loaded, mode:', data.transcriptionMode || 'B')
      }
    } catch (err) {
      console.warn('⚠️ AGC: Could not load voice config:', err)
      // AGC: Keep defaults on error
    } finally {
      setIsLoading(false)
    }
  }, [backendUrl, getAuthToken])

  // AGC: Fetch whisper quota from backend to determine Mode A vs B for FREE users
  const fetchQuota = useCallback(async () => {
    if (subscriptionTier !== 'free') {
      setWhisperExhausted(false)
      return
    }
    try {
      const token = await getAuthToken()
      if (!token) return
      const response = await fetch(`${backendUrl}/api/v1/voice/quota`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        const exhausted = data.whisperExhausted === true
        setWhisperExhausted(exhausted)
        const monthKey = new Date().toISOString().slice(0, 7)
        if (exhausted) {
          localStorage.setItem(`droidfarm_whisperExhausted_${monthKey}`, 'true')
        } else {
          localStorage.removeItem(`droidfarm_whisperExhausted_${monthKey}`)
        }
      }
    } catch (err) {
      console.warn('AGC: Could not fetch voice quota:', err)
    }
  }, [backendUrl, getAuthToken, subscriptionTier])

  // AGC: Called when voice upload returns 429 CapReached
  const markWhisperExhausted = useCallback(() => {
    setWhisperExhausted(true)
    const monthKey = new Date().toISOString().slice(0, 7)
    localStorage.setItem(`droidfarm_whisperExhausted_${monthKey}`, 'true')
  }, [])

  // AGC: Update config on backend
  const updateConfig = useCallback(async (updates: Partial<VoiceConfig>) => {
    try {
      const token = await getAuthToken()
      if (!token) return

      const response = await fetch(`${backendUrl}/api/v1/voice/config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...config, ...updates }),
      })

      if (response.ok) {
        const data = await response.json()
        setConfig({ ...DEFAULT_VOICE_CONFIG, ...data })
        console.log('✅ AGC: Voice config updated')
      }
    } catch (err) {
      console.error('❌ AGC: Error updating voice config:', err)
      setError('Erro ao atualizar configurações de voz')
    }
  }, [backendUrl, config, getAuthToken])

  // AGC: Set voice mode (shortcut for common operation)
  const setMode = useCallback(async (mode: VoiceTranscriptionMode) => {
    await updateConfig({ transcriptionMode: mode })
  }, [updateConfig])

  // AGC: Update queue state
  const updateQueueState = useCallback(() => {
    if (userId) {
      const commands = voiceQueue.getQueuedCommands(userId)
      setQueuedCommands(commands)
      setQueueCount(voiceQueue.getPendingCount(userId))
    }
  }, [userId])

  // AGC: Process offline queue
  const processQueue = useCallback(async (): Promise<{ processed: number; failed: number }> => {
    if (!userId || !isOnline) {
      return { processed: 0, failed: 0 }
    }

    setIsProcessingQueue(true)
    console.log('🔄 AGC: Processing voice queue...')

    try {
      const token = await getAuthToken()
      if (!token) {
        return { processed: 0, failed: 0 }
      }

      const result = await voiceQueue.processQueuedCommands(userId, token, backendUrl)
      updateQueueState()

      console.log(`📊 AGC: Queue processed: ${result.processed} success, ${result.failed} failed`)
      return { processed: result.processed, failed: result.failed }
    } catch (err) {
      console.error('❌ AGC: Error processing queue:', err)
      setError('Erro ao processar fila de comandos')
      return { processed: 0, failed: 0 }
    } finally {
      setIsProcessingQueue(false)
    }
  }, [userId, isOnline, getAuthToken, backendUrl, updateQueueState])

  // AGC: Clear queue
  const clearQueue = useCallback(() => {
    if (userId) {
      voiceQueue.clearQueue(userId)
      updateQueueState()
      console.log('🧹 AGC: Voice queue cleared')
    }
  }, [userId, updateQueueState])

  // AGC: Clear error
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // AGC: Online/offline event listeners
  useEffect(() => {
    let onlineTimeoutId: ReturnType<typeof setTimeout> | null = null

    const handleOnline = async () => {
      console.log('🌐 AGC: Connection restored')
      setIsOnline(true)

      // AGC: Auto-process queue if enabled
      if (config.autoProcessQueue && userId) {
        // AGC: Small delay to ensure connection is stable
        onlineTimeoutId = setTimeout(() => {
          processQueue()
        }, 1000)
      }
    }

    const handleOffline = () => {
      console.log('📴 AGC: Connection lost')
      setIsOnline(false)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      if (onlineTimeoutId) clearTimeout(onlineTimeoutId)
    }
  }, [config.autoProcessQueue, userId, processQueue])

  // AGC: Load config on auth change
  useEffect(() => {
    if (user?.uid) {
      reloadConfig()
      updateQueueState()
      fetchQuota()
    }
  }, [user?.uid, reloadConfig, updateQueueState, fetchQuota])

  // AGC: Periodic queue update (every 30 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      updateQueueState()
    }, 30000)

    return () => clearInterval(interval)
  }, [updateQueueState])

  // AGC: Calculate effective mode based on subscription tier and Whisper quota
  const effectiveMode = useMemo(() => {
    return getEffectiveVoiceMode(subscriptionTier, config.transcriptionMode, isOnline, whisperExhausted)
  }, [subscriptionTier, config.transcriptionMode, isOnline, whisperExhausted])

  // AGC: Context value
  const value: VoiceContextType = {
    config,
    mode: effectiveMode, // AGC: Use effective mode based on tier, quota, and online state
    setMode,
    updateConfig,
    reloadConfig,
    isOnline,
    whisperExhausted,
    markWhisperExhausted,
    queueCount,
    queuedCommands,
    processQueue,
    clearQueue,
    lastResult,
    setLastResult,
    isLoading,
    isProcessingQueue,
    error,
    clearError,
  }

  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>
}

/**
 * AGC: Hook to use Voice context
 */
export const useVoiceContext = (): VoiceContextType => {
  const context = useContext(VoiceContext)
  if (!context) {
    throw new Error('AGC: useVoiceContext must be used within VoiceProvider')
  }
  return context
}

/**
 * AGC: Hook for just the queue count (lightweight)
 */
export const useVoiceQueueCount = (): number => {
  const { queueCount } = useVoiceContext()
  return queueCount
}

/**
 * AGC: Hook for just the online status
 */
export const useVoiceOnlineStatus = (): boolean => {
  const { isOnline } = useVoiceContext()
  return isOnline
}

/**
 * AGC: Hook for voice mode
 */
export const useVoiceMode = (): {
  mode: VoiceTranscriptionMode
  setMode: (mode: VoiceTranscriptionMode) => Promise<void>
  isPaid: boolean
} => {
  const { mode, setMode } = useVoiceContext()
  return {
    mode,
    setMode,
    isPaid: mode === 'A',
  }
}

export default VoiceProvider

